import type { HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { catchError, map, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { StorageService } from '../auth/storage.service';
import { Router } from '@angular/router';
import { DataService } from '../../shared/services/data.service';

/**
 * Joins an array of messages into a single multi-line string.
 * Returns the first message if there is only one.
 */
function joinMessages(messages: string[]): string {
  if (messages.length === 0) return '';
  if (messages.length === 1) return messages[0];
  return messages.join('\n');
}

/**
 * Extracts a human-readable error message from the API response.
 *
 * The API returns errors primarily in this shape:
 *   { "Error": ["The email field must be a valid email address."] }
 *
 * But we still try a few other common shapes as a safety net.
 */
function extractErrorMessage(error: HttpErrorResponse): string {
  // Client-side / network error (status 0)
  if (error.status === 0) {
    return 'Unable to reach the server. Please check your internet connection and try again.';
  }

  const body: any = error.error;

  // Plain string body
  if (typeof body === 'string' && body.trim().length > 0) {
    return body;
  }

  if (body && typeof body === 'object') {
    // Primary format: { "Error": ["msg1", "msg2", ...] }
    if (Array.isArray(body.Error) && body.Error.length > 0) {
      return joinMessages(body.Error.map((m: any) => String(m)));
    }
    if (typeof body.Error === 'string' && body.Error.trim().length > 0) {
      return body.Error;
    }

    // Lowercase variant of the above: { error: ['msg1', ...] }
    if (Array.isArray(body.error) && body.error.length > 0) {
      return joinMessages(body.error.map((m: any) => String(m)));
    }
    if (typeof body.error === 'string' && body.error.trim().length > 0) {
      return body.error;
    }

    // { message: '...' }
    if (typeof body.message === 'string' && body.message.trim().length > 0) {
      return body.message;
    }

    // { data: { message: '...' } }
    if (body.data && typeof body.data.message === 'string' && body.data.message.trim().length > 0) {
      return body.data.message;
    }

    // { error: { message: '...' } }
    if (body.error && typeof body.error === 'object' && typeof body.error.message === 'string') {
      return body.error.message;
    }

    // Laravel-style validation: { errors: { field: ['msg'] } }
    if (body.errors && typeof body.errors === 'object') {
      const messages: string[] = [];
      for (const key of Object.keys(body.errors)) {
        const value = (body.errors as any)[key];
        if (Array.isArray(value)) {
          for (const m of value) messages.push(`${key}: ${String(m)}`);
        } else if (typeof value === 'string') {
          messages.push(`${key}: ${value}`);
        }
      }
      if (messages.length > 0) return joinMessages(messages);
    }
  }

  // Fallback to status-text based message
  return defaultMessageForStatus(error.status);
}

function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 400: return 'Bad request. Please review your input and try again.';
    case 401: return 'You don\'t have permission to access this page. Please login again.';
    case 403: return 'You are not authorized to perform this action.';
    case 404: return 'The requested resource was not found.';
    case 405: return 'The requested action is not allowed.';
    case 409: return 'A conflict occurred. The resource may already exist.';
    case 422: return 'The submitted data is invalid. Please review and try again.';
    case 429: return 'Too many requests. Please slow down and try again later.';
    case 500: return 'A server error occurred. Please try again later.';
    case 502: return 'The server is temporarily unavailable. Please try again later.';
    case 503: return 'The service is currently unavailable. Please try again later.';
    case 504: return 'The server took too long to respond. Please try again later.';
    default: return 'Something went wrong. Please try again.';
  }
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  const dataService = inject(DataService);
  const router = inject(Router);

  return next(req).pipe(
    map((event: HttpEvent<any>) => event),
    catchError((error: HttpErrorResponse) => {
      const message = extractErrorMessage(error);

      switch (error.status) {
        case 401:
          // Unauthorized -> clear session and force re-login
          storage.removeToken();
          dataService.showMsg('fail', message);
          router.navigate(['auth/login']);
          break;

        case 403:
          // Forbidden -> notify but do not logout
          dataService.showMsg('fail', message);
          break;

        case 404:
          // Not found -> silent for list-style requests, notify for actions
          if (req.method !== 'GET') {
            dataService.showMsg('fail', message);
          }
          break;

        case 422:
        case 400:
        case 409:
        case 405:
          // Validation / client errors -> always show the message
          dataService.showMsg('fail', message);
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors -> notify
          dataService.showMsg('fail', message);
          break;

        case 0:
          // Network / unreachable -> notify
          dataService.showMsg('fail', message);
          break;

        default:
          // Any other error -> notify
          dataService.showMsg('fail', message);
          break;
      }

      // Always make sure the global loading spinner is hidden on failure
      dataService.showLoading(false);

      return throwError(() => error);
    })
  );
};
